import type { PaginatedResponse } from '@/types';
import { type Ref } from 'vue';

export interface PaginationLoaderDelegate<Model> {
    loadPage(page: number): Promise<PaginatedResponse<Model[]>>;

    getModels(): Ref<Model[]>;

    afterClear?(): void;
}

export class PaginationLoader<Model> {
    public lastPagination: PaginatedResponse<Model[]> | null = null;
    public identifier = 0;
    private timeoutId: ReturnType<typeof setTimeout> | null = null;

    private currentPageRequests: Set<number> = new Set<number>();
    private enabled: boolean = false;

    constructor(private readonly loader: PaginationLoaderDelegate<Model>) {
        //
    }
    public disable() {
        this.enabled = false;
    }
    public async enable() {
        this.enabled = true;
        await this.reload(false);
    }

    private async loadPage(page: number): Promise<void> {
        return await this.loader.loadPage(page).then((response) => {
            this.lastPagination = response;
            this.loader.getModels().value.push(...response.data);
        });
    }

    private canLoadMore(): boolean {
        if (!this.enabled) {
            return false;
        }
        if (!this.lastPagination) {
            return true;
        }

        return this.lastPagination.meta.current_page < this.lastPagination.meta.last_page;
    }

    public async reload(delayed: boolean = false): Promise<void> {
        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
        }

        if (delayed) {
            this.timeoutId = setTimeout(async () => {
                await this.doReload();
            }, 500);
        } else {
            await this.doReload();
        }
    }

    private async doReload(): Promise<void> {
        this.loader.getModels().value = [];
        this.loader.afterClear?.();
        this.lastPagination = null;
        this.identifier++;
    }

    public static isPagnationLoader(value: unknown): value is PaginationLoader<unknown> {
        return value instanceof PaginationLoader;
    }
}
